/**
 * Tenant branding. One bundle is served to every site TIK Mail is enabled on, so anything
 * site-specific has to be resolved at runtime from `GET /api/tenant`.
 *
 * Three rules keep this off the critical path:
 *  1. A guess from `location.hostname` is available synchronously, before any request — on
 *     `mail.x.com` it already yields `you@x.com`, so nothing flashes and nothing waits.
 *  2. The last answer is cached in localStorage per origin and applied on the next cold start.
 *  3. The fetch is fire-and-forget: a failure leaves the guess in place. Branding is never a
 *     reason for the mail client not to open.
 */
import { api } from '$lib/api/client';
import type { TenantInfo } from '$lib/api/types';

export type { TenantInfo };

const LS_KEY = 'tik.tenant.v1';

/** `mail.andls.sa` → `andls.sa`; anything else is returned unchanged. */
export function mailDomainOf(host: string): string {
  const h = (host || '').toLowerCase().split(':')[0] ?? '';
  const stripped = h.replace(/^(mail|webmail|www)\./, '');
  return stripped.includes('.') ? stripped : h;
}

/** What to show before the network answers (and if it never does). */
export function guessTenant(host: string): TenantInfo {
  const domain = mailDomainOf(host);
  return { host: host.split(':')[0] ?? host, known: false, domains: [domain], loginHint: `you@${domain}` };
}

function readCache(): TenantInfo | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as TenantInfo;
    // a cache written by another origin (shared device, copied profile) must not brand this one
    if (!v || typeof v.loginHint !== 'string' || v.host !== location.hostname) return null;
    return v;
  } catch {
    return null;
  }
}

class TenantStore {
  info = $state<TenantInfo>(
    typeof location === 'undefined' ? guessTenant('') : guessTenant(location.hostname),
  );

  /** `you@domain` placeholder for the login field. */
  get loginHint(): string {
    return this.info.loginHint;
  }

  /** Site name, or `null` when this tenant uses the product's own name. */
  get name(): string | null {
    return this.info.name ?? null;
  }

  /** Apply a fresh or cached descriptor to the store and the document. */
  apply(info: TenantInfo): void {
    this.info = info;
    if (typeof document === 'undefined') return;
    if (info.name) document.title = info.name;
    if (info.themeColor) {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', info.themeColor);
    }
  }

  /** Paint from cache, then refresh in the background. Never throws, never blocks. */
  boot(): void {
    const cached = readCache();
    if (cached) this.apply(cached);
    void this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const info = await api.tenant();
      if (!info || typeof info.loginHint !== 'string') return;
      this.apply(info);
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(info));
      } catch {
        /* private mode / quota */
      }
    } catch {
      /* offline or bridge down: the guess (or cache) stands */
    }
  }
}

export const tenant = new TenantStore();
